/**
 * CEP (postal code) lookup via BrasilAPI (free, public, CORS-open). Used to
 * autofill the address fields in onboarding when the user types the CEP first.
 *
 * Docs: https://brasilapi.com.br/docs#tag/CEP-V2
 */

const BRASILAPI_CEP = 'https://brasilapi.com.br/api/cep/v2';

export interface CepLookupResult {
    cep: string;        // formatted "00000-000"
    street: string;     // logradouro
    neighborhood: string;
    city: string;
    state: string;      // UF
}

function onlyDigits(value: string): string {
    return (value || '').replace(/\D+/g, '');
}

export function formatCep(value: string): string {
    const d = onlyDigits(value);
    if (d.length !== 8) return value;
    return `${d.slice(0, 5)}-${d.slice(5)}`;
}

/** City + province from a postal code (no street — free tiers don't expose it). */
export interface PostalLookupResult {
    city: string;
    state: string;
}

/**
 * Canadian postal-code lookup (city + province) via Zippopotam.us — free, public,
 * CORS-open. Uses the FSA (first 3 chars, e.g. "V3K"). Street-level data isn't in
 * any free tier (that needs Canada Post AddressComplete), so the owner types the
 * street; we just prefill city + province. Returns null on a miss.
 */
export async function lookupCaPostal(postal: string): Promise<PostalLookupResult | null> {
    const fsa = (postal || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 3);
    if (fsa.length < 3) return null;
    try {
        const res = await fetch(`https://api.zippopotam.us/CA/${fsa}`, { headers: { Accept: 'application/json' } });
        if (!res.ok) return null;
        const d = await res.json();
        const p = Array.isArray(d.places) && d.places.length > 0 ? d.places[0] : null;
        if (!p) return null;
        return {
            city: (p['place name'] || '').trim(),
            state: (p['state abbreviation'] || p['state'] || '').trim(),
        };
    } catch {
        return null;
    }
}

/** Format a Canadian postal code as "A1A 1A1" as the user types. */
export function formatCaPostal(value: string): string {
    const s = (value || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
    return s.length > 3 ? `${s.slice(0, 3)} ${s.slice(3)}` : s;
}

/** Look up a CEP. Returns null when not found / invalid (caller just leaves the
 *  fields for manual entry — a CEP miss must never block onboarding). */
export async function lookupCep(cep: string): Promise<CepLookupResult | null> {
    const digits = onlyDigits(cep);
    if (digits.length !== 8) return null;
    try {
        const res = await fetch(`${BRASILAPI_CEP}/${digits}`, {
            headers: { Accept: 'application/json' },
        });
        if (!res.ok) return null;
        const d = await res.json();
        return {
            cep: formatCep(digits),
            street: (d.street || '').trim(),
            neighborhood: (d.neighborhood || '').trim(),
            city: (d.city || '').trim(),
            state: (d.state || '').trim(),
        };
    } catch {
        return null;
    }
}
